import { useMemo } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { usePageMeta } from '@/app/useSeo'
import { useLang } from '@/i18n/useLang'
import { useLocalized } from '@/i18n/useLocalized'
import { WORKSPACES } from '@/shared/ui/workspaces'

/**
 * Страница, которой нет.
 *
 * СТАВИТ `noindex`, и это не украшение. Адрес, которого нет в маршрутах,
 * получает от эджа оболочку приложения с кодом 200 (иначе обновление страницы
 * внутри кабинета было бы 404), поэтому «страницы нет» — единственное, чем эта
 * страница может сказать поисковику правду. Без этого любой битый чужой линк
 * попадал в индекс под заголовком главной: `metaFor` для незнакомого пути
 * возвращает домашнюю запись.
 *
 * Рисуется ВЕЗДЕ, где адреса нет: в корне, внутри витрины (с её шапкой и
 * подвалом), внутри кабинета и зоны клиента — роутер отдаёт промах ближайшему
 * разделу (`defaultNotFoundComponent`). Поэтому стили здесь свои, строчные:
 * лист витрины в кабинете не действует. Язык — языка раздела: на витрине из
 * адреса, в приложении — выбранный.
 *
 * Три двери, а не одна «На главную»: человек, промахнувшийся адресом, шёл
 * куда-то конкретно, и чаще всего — к психологу или в свои встречи.
 */
export function NotFoundPage() {
  const { t, lang } = useLang()
  const L = useLocalized()
  const path = useRouterState({ select: (s) => s.location.pathname })
  usePageMeta(
    useMemo(() => ({ title: t.notFound.title, description: t.notFound.body, noindex: true }), [t]),
  )
  return (
    <section style={{ minHeight: '70vh', display: 'grid', placeItems: 'center', padding: 'var(--space-8) var(--space-4)' }}>
      <div style={{ textAlign: 'center', display: 'grid', gap: 'var(--space-4)', maxWidth: '36rem', minWidth: 0 }}>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1, textWrap: 'balance' }}>
          {t.notFound.title}
        </h1>
        <p style={{ color: 'var(--color-text-muted)' }}>{t.notFound.body}</p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-2)',
          }}
        >
          <Link to={L('/')} className="btn btn-primary btn-md">
            {t.notFound.cta}
          </Link>
          <Link to={L('/find-psychologist')} className="btn btn-outline btn-md">
            {t.nav.findPsychologist}
          </Link>
          <Link to={WORKSPACES.meetings.path} className="btn btn-ghost btn-md">
            {WORKSPACES.meetings.label[lang]}
          </Link>
        </div>
        {/* Адрес, по которому пришли: опечатку в нём видно сразу. Под
            заголовком, а не над ним — над заголовком он был бы кикером. */}
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-faint)',
            overflowWrap: 'anywhere',
          }}
        >
          404 · {path}
        </p>
      </div>
    </section>
  )
}
